// Domain types for the Rules Manager. Backed by the real
// `rule-service` — RuleType/RuleSeverity/RuleValidationMethod/RuleLifecycle are all
// canonical in `@atlas/types` (backend + frontend share one source of truth),
// re-exported here so existing imports from this module keep working.
import type { DTOOf, IRule, IRuleMetadataCheck } from '@atlas/types'
import {
	RuleElementType,
	RuleLifecycle,
	RuleMetadataOperator,
	RuleSeverity,
	RuleType,
	RuleValidationMethod,
} from '@atlas/types'

export { RuleElementType, RuleLifecycle, RuleMetadataOperator, RuleSeverity, RuleType, RuleValidationMethod }

import type { Selection } from './work-order.types'

export const RULE_LIFECYCLE_LABEL: Record<RuleLifecycle, string> = {
	[RuleLifecycle.DRAFT]: 'Draft',
	[RuleLifecycle.ACTIVE]: 'Active',
	[RuleLifecycle.RETIRED]: 'Retired',
}

/** Border + text color per lifecycle: draft = neutral, active = success, retired = warning (matches Archive). */
export const RULE_LIFECYCLE_COLOR: Record<RuleLifecycle, string> = {
	[RuleLifecycle.DRAFT]: 'border-ds-border text-ds-text-muted',
	[RuleLifecycle.ACTIVE]: 'border-ds-success text-ds-success',
	[RuleLifecycle.RETIRED]: 'border-ds-warning text-ds-warning',
}

export const RULE_SEVERITY_LABEL: Record<RuleSeverity, string> = {
	[RuleSeverity.BLOCKING]: 'Blocking',
	[RuleSeverity.WARNING]: 'Warning',
}

/** Explorer facet ordering. */
export const RULE_TYPE_ORDER: readonly RuleType[] = [
	RuleType.BRAND,
	RuleType.CHANNEL,
	RuleType.REGIONAL,
	RuleType.COMPLIANCE,
] as const

export const RULE_SEVERITY_ORDER: readonly RuleSeverity[] = [RuleSeverity.BLOCKING, RuleSeverity.WARNING] as const

export const RULE_ELEMENT_TYPE_LABEL: Record<RuleElementType, string> = {
	[RuleElementType.TEXT]: 'Text',
	[RuleElementType.FILE]: 'File',
	[RuleElementType.IMAGE]: 'Image',
	[RuleElementType.VIDEO]: 'Video',
	[RuleElementType.WEBSITE]: 'Website',
	[RuleElementType.CONTACT]: 'Contact',
}

/** Authoring-control ordering: the text/asset kinds authors reach for most, first. */
export const RULE_ELEMENT_TYPE_ORDER: readonly RuleElementType[] = [
	RuleElementType.TEXT,
	RuleElementType.IMAGE,
	RuleElementType.VIDEO,
	RuleElementType.FILE,
	RuleElementType.WEBSITE,
	RuleElementType.CONTACT,
] as const

/**
 * Human-readable applicability for a rule. An empty list is rendered as an explicit
 * "applies to all element types" rather than as a blank, so an unscoped rule never
 * looks like missing data.
 */
export function appliesToLabel(appliesTo: RuleElementType[]): string {
	if (appliesTo.length === 0) return 'All element types'
	return [...RULE_ELEMENT_TYPE_ORDER]
		.filter((t) => appliesTo.includes(t))
		.map((t) => RULE_ELEMENT_TYPE_LABEL[t])
		.join(' · ')
}

/** Display label for the create-form type selector (mockup's " rule" suffix). */
export const ruleTypeLabel = (type: RuleType): string => `${type} rule`

/** A governance rule classified against the axis registry. */
export interface Rule {
	id: string
	statement: string
	type: RuleType
	severity: RuleSeverity
	/** Which context the rule applies to (axis code → value codes). Empty = applies to all. */
	classification: Selection
	/** The executable instruction the validation agent runs (prose alone is insufficient). */
	checkDefinition: string
	validationMethod: RuleValidationMethod
	/** The deterministic check a `metadata` rule is answered by; null is legal and reads as uncheckable. */
	metadataCheck?: IRuleMetadataCheck | null
	/** Element types the rule governs. Empty = applies to all element types. */
	appliesTo: RuleElementType[]
	lifecycle: RuleLifecycle
	effectiveFrom: string // ISO date
	effectiveTo: string | null // ISO date or open-ended
	createdAt: string // ISO date
}

/**
 * A rule resolved by coverage matching for some context — `GET /rules/applicable`.
 * Distinct from `Rule` because that endpoint returns the bare rule DTO: callers ask
 * "what applies HERE?", so the rule's own tuple is not part of the answer and the
 * response carries no `classification`.
 */
export type ApplicableRule = DTOOf<IRule>

/**
 * Body for creating a rule. New rules land in draft. Mirrors the backend's
 * Zod-inferred `RuleCreateInput` (`@atlas/models`) field-for-field — the frontend
 * can't import that type directly (it's inferred from a backend-only schema), so
 * keep this shape in sync by hand when the backend schema changes.
 */
export interface CreateRuleInput {
	statement: string
	type: RuleType
	severity: RuleSeverity
	classification: Selection
	appliesTo?: RuleElementType[]
	checkDefinition?: string
	validationMethod?: RuleValidationMethod
	metadataCheck?: IRuleMetadataCheck | null
}

/** Namespaces a classification axis code into the metadata-check field path a
 *  `metadata` rule resolves against (must match `CLASSIFICATION_PREFIX` in
 *  `@atlas/core-utils/ai-prompt/subject-metadata`, the backend's single source). */
export const CLASSIFICATION_CHECK_PREFIX = 'classification.'

export const RULE_METADATA_OPERATOR_ORDER: readonly RuleMetadataOperator[] = [
	RuleMetadataOperator.PRESENT,
	RuleMetadataOperator.ABSENT,
] as const

const AXIS_ORDER = ['brand', 'geography', 'species', 'channel', 'file_type']
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * Derive a human-readable scope label from a classification tuple — the single
 * source for the explorer Scope facet, rule cards, and detail (never stored, so it
 * can never disagree with the tuple). Empty tuple → "Applies to all". `valueLabels`
 * maps a value code to its registry label (falls back to a capitalized code).
 */
export function scopeLabel(tuple: Selection, valueLabels: Record<string, string> = {}): string {
	const axes = Object.keys(tuple).filter((a) => (tuple[a]?.length ?? 0) > 0)
	if (axes.length === 0) return 'Applies to all'
	const rank = (a: string) => {
		const i = AXIS_ORDER.indexOf(a)
		return i < 0 ? AXIS_ORDER.length : i
	}
	return [...axes]
		.sort((a, b) => rank(a) - rank(b))
		.flatMap((a) => (tuple[a] ?? []).map((v) => valueLabels[v] ?? cap(v)))
		.join(' · ')
}
